// Type for the return value of getLayoutForSport
export type LayoutForSportResponse = {
    layoutData: ILayoutStructureModel;
    minHeights: LayoutResults;
};

export enum ComponentNames {
    HEADER = 'header',
    SWITCHERS = 'switchers',
    QUICK_LINK_MODULE = 'QuickLinkModule',
    INPLAY_MODULE = 'InplayModule',
    POPULAR_ACCA_MODULE = 'PopularAccaModule',
    BYB_WIDGET_MODULE = 'BybWidgetModule',
    SURFACE_BET_MODULE = 'SurfaceBetModule',
    HIGHLIGHT_CAROUSEL_MODULE = 'HighlightCarouselModule',
    MATCH_RESULTS = 'matchResult',
    PRICE_BOOST_MULTIPLE_MODULE = 'PriceBoostMultiplesModule',
    PRICE_BOOST_SINGLE_MODULE = 'PriceBoostSinglesModule',
    PRE_CREATED_BET_BUILDER_MODULE = 'PreCreatedBetBuilderModule',
    EXTRA_PLACE_RACES = 'EPR',
    NEXT_RACES = 'NextRaces',
    VIRUAL_CAROUSEL = 'VirtualRaceModule',
    VIRTUAL_RACES = 'LVR',
    INTERNATIONAL_RACES = 'IR',
    UK_AND_IRISH_RACES = 'UIR',
}
export interface ILayoutStructureModel {
    modules: Module[];
    pageId?: string;
}

export interface Module {
    type: string; // The name of the module, e.g., "SurfaceBetModule"
    noOfItems: number; // The number of items in the module
    noOfItemsList?: [{ typeName: string; count: number; items: Array<{ earlyPriceAvailable?: boolean }> }];
    filtersEnabled?: boolean;
    racingType?: string; // Type of racing, e.g., "UK", "IR", "UIR", "NextRaces"
}

export interface IItem {
    earlyPriceAvailable?: boolean;
}

// Create a list of components and infer the types
const components = Object.values(ComponentNames);

/**
 * Represents the calculated layout results for various components.
 */
export type LayoutResults = Partial<{
    [component in (typeof components)[number]]: number;
}>;

/**
 * Represents the calculation methods for determining component heights.
 * - 'static': Used for components with a single item, where the height is not multiplied.
 * - 'perItem': Multiplies the base height by the number of items in the component.
 * - 'perHalfSize': Multiplies the base height by half the number of items (rounded up).
 * - 'perLeague': Used for hierarchical data (e.g., leagues and matches).
 */
export type CalculationMethod = 'static' | 'perItem' | 'perHalfSize' | 'perLeague' | 'perRaceContent' | 'perMeeting';

/**
 * Represents the unit of measurement for component heights.
 * - 'px': Fixed height in pixels.
 * - 'vw': Height calculated as a percentage of the viewport width.
 */
export type Unit = 'px' | 'vw';

/**
 * Defines the height configuration for components.
 */
export type ComponentHeightConfig = {
    [componentName: string]: BrandHeightConfig;
};

/**
 * Defines the height configuration for different brands.
 */
export type BrandHeightConfig = {
    ladbrokes: PlatformHeightConfig;
    coral: PlatformHeightConfig;
    [key: string]: PlatformHeightConfig;
};

/**
 * Defines the height configuration for different platforms.
 * - `desktop` is optional because support for it is currently not needed.
 *   Future support may be extended if required.
 */
export type PlatformHeightConfig = {
    mobile: ComponentHeightProperties;
    desktop?: ComponentHeightProperties;
};

/**
 * Represents the height properties for a component.
 * - `headerHeight`: Height of the header for the component.
 * - `baseHeight`: Base height of the component used for calculations.
 * - `unit`: Unit of measurement for the component's height.
 * - `calculationMethod`: Method used to calculate the component's height.
 * - `sectionHeight`: height of a section of the component used for calculations.
 */
export type ComponentHeightProperties = {
    headerHeight: number;
    subHeaderHeight?: number; // Optional height for subheader (used in hierarchical layouts).
    baseHeight: number;
    sectionHeight?: number;
    unit: Unit;
    switcherHeight?: number; // Optional height for switchers
    meetingHeight?: number; // Optional height for meetings
    earlypriceHeight?: number; // Optional height for early price
    calculationMethod: CalculationMethod;
};

// Helper type for deep partial overrides.
type DeepPartial<T> = {
    [P in keyof T]?: DeepPartial<T[P]>;
};

// Define defaults for base height and calculation methods
const defaultComponentHeight: BrandHeightConfig = {
    ladbrokes: {
        mobile: {
            baseHeight: 40,
            unit: 'px',
            calculationMethod: 'static',
            headerHeight: 0,
        },
    },
    coral: {
        mobile: {
            baseHeight: 40,
            unit: 'px',
            calculationMethod: 'static',
            headerHeight: 0,
        },
    },
};

const defaultMeetingsHeight: Partial<ComponentHeightProperties> = {
    baseHeight: 8, // margin for section
    headerHeight: 40,
    switcherHeight: 63,
    subHeaderHeight: 32, //included section margin
    meetingHeight: 50,
    earlypriceHeight: 13,
    calculationMethod: 'perMeeting',
};

// Define overrides for specific components
const componentOverrides: Record<string, DeepPartial<BrandHeightConfig>> = {
    header: {
        ladbrokes: {
            mobile: { baseHeight: 42 },
        },
        coral: {
            mobile: { baseHeight: 40 },
        },
    },
    HighlightCarouselModule: {
        ladbrokes: {
            mobile: {
                baseHeight: 194,
            },
        },
        coral: {
            mobile: {
                baseHeight: 194,
            },
        },
    },
    SurfaceBetModule: {
        ladbrokes: {
            mobile: {
                baseHeight: 111, //8 padding 8 margin 95 component height
            },
        },
        coral: {
            mobile: {
                baseHeight: 111, //8 padding 8 margin 95 component height
            },
        },
    },
    PriceBoostSinglesModule: {
        ladbrokes: {
            mobile: {
                baseHeight: 142,
            },
        },
        coral: {
            mobile: {
                baseHeight: 146, // accordion margin 4px
            },
        },
    },
    BybWidgetModule: {
        ladbrokes: {
            mobile: {
                baseHeight: 400,
            },
        },
        coral: {
            mobile: {
                baseHeight: 400,
            },
        },
    },
    QuickLinkModule: {
        ladbrokes: {
            mobile: {
                baseHeight: 40,
                calculationMethod: 'perHalfSize',
            },
        },
        coral: {
            mobile: {
                baseHeight: 40,
                calculationMethod: 'perHalfSize',
            },
        },
    },
    InplayModule: {
        ladbrokes: {
            mobile: {
                headerHeight: 46,
                subHeaderHeight: 30,
                baseHeight: 85,
                calculationMethod: 'perLeague',
            },
        },
        coral: {
            mobile: {
                headerHeight: 46,
                subHeaderHeight: 30,
                baseHeight: 85,
                calculationMethod: 'perLeague',
            },
        },
    },
    PopularAccaModule: {
        ladbrokes: {
            mobile: {
                headerHeight: 168.86,
                baseHeight: 50.67,
                calculationMethod: 'perItem',
            },
        },
        coral: {
            mobile: {
                headerHeight: 168.86,
                baseHeight: 50.67,
                calculationMethod: 'perItem',
            },
        },
    },
    PreCreatedBetBuilderModule: {
        ladbrokes: {
            mobile: {
                headerHeight: 183.47,
                baseHeight: 36,
                calculationMethod: 'perItem',
            },
        },
        coral: {
            mobile: {
                headerHeight: 176.17,
                baseHeight: 40.14,
                calculationMethod: 'perItem',
            },
        },
    },
    PriceBoostMultiplesModule: {
        ladbrokes: {
            mobile: {
                headerHeight: 178,
                baseHeight: 51,
                calculationMethod: 'perItem',
            },
        },
        coral: {
            mobile: {
                headerHeight: 178,
                baseHeight: 51,
                calculationMethod: 'perItem',
            },
        },
    },
    matchResult: {
        ladbrokes: {
            mobile: {
                baseHeight: 400,
            },
        },
        coral: {
            mobile: {
                baseHeight: 400,
            },
        },
    },
    EPR: {
        ladbrokes: {
            mobile: {
                baseHeight: 400,
            },
        },
        coral: {
            mobile: {
                baseHeight: 167.5,
            },
        },
    },
    NextRaces: {
        ladbrokes: {
            mobile: {
                baseHeight: 400,
            },
        },
        coral: {
            mobile: {
                baseHeight: 93,
                headerHeight: 52,
                subHeaderHeight: 35,
                sectionHeight: 81,
                calculationMethod: 'perRaceContent',
            },
        },
    },
    LVR: {
        ladbrokes: {
            mobile: {
                baseHeight: 400,
            },
        },
        coral: {
            mobile: defaultMeetingsHeight,
        },
    },
    IR: {
        ladbrokes: {
            mobile: {
                baseHeight: 400,
            },
        },
        coral: {
            mobile: defaultMeetingsHeight,
        },
    },
    UIR: {
        ladbrokes: {
            mobile: {
                baseHeight: 400,
            },
        },
        coral: {
            mobile: defaultMeetingsHeight,
        },
    },
    VirtualRaceModule: {
        ladbrokes: {
            mobile: {
                baseHeight: 181,
            },
        },
        coral: {
            mobile: {
                baseHeight: 181,
            },
        },
    },
};

// Function to merge defaults with overrides
const createComponentHeights = (): ComponentHeightConfig =>
    components.reduce((acc: any, componentName: any) => {
        acc[componentName] = {
            ladbrokes: {
                mobile: {
                    ...defaultComponentHeight.ladbrokes.mobile,
                    ...componentOverrides[componentName]?.ladbrokes?.mobile,
                },
            },
            coral: {
                mobile: {
                    ...defaultComponentHeight.coral.mobile,
                    ...componentOverrides[componentName]?.coral?.mobile,
                },
            },
        };
        return acc;
    }, {});

// Export the generated heights
export const componentHeights: ComponentHeightConfig = createComponentHeights();
